import { midpoint, type CostRange } from '@/lib/pricing/models';
import type { AppCostResult } from '@/lib/pricing/compute';

type ChartPoint = {
  year: number;
  value: number;
  range: CostRange;
};

type ChartSeries = {
  slug: string;
  name: string;
  shortName: string;
  color: string;
  points: ChartPoint[];
};

const COLORS = ['#018B9C', '#B45309', '#6D28D9'];

const WIDTH = 760;
const HEIGHT = 340;
const PADDING = { top: 16, right: 24, bottom: 36, left: 56 };

function formatAxis(value: number): string {
  if (value >= 1000) {
    return `$${Math.round(value / 100) / 10}k`;
  }
  return `$${Math.round(value)}`;
}

function buildSeries(
  results: AppCostResult[],
  slugs: string[],
  years: number
): ChartSeries[] {
  const bySlug = new Map(results.map((result) => [result.app.slug, result]));

  return slugs.flatMap((slug, index) => {
    const result = bySlug.get(slug);
    if (!result?.covered || !result.annual) return [];

    const points = result.cumulative.slice(0, years).map((point) => ({
      year: point.year,
      value: midpoint({ low: point.low, high: point.high }),
      range: { low: point.low, high: point.high },
    }));

    return [
      {
        slug,
        name: result.app.name,
        shortName: result.app.shortName,
        color: COLORS[index] ?? COLORS[COLORS.length - 1],
        points,
      },
    ];
  });
}

export function CostOverTimeChart({
  results,
  years,
  compare,
  onCompareChange,
}: {
  results: AppCostResult[];
  years: number;
  compare: string;
  onCompareChange: (slug: string) => void;
}) {
  const visibleYears = Math.max(1, Math.min(5, years));
  const competitors = results.filter(
    (result) => result.app.slug !== 'harbor' && result.covered && result.annual
  );
  const available = competitors.map((result) => result.app.slug);

  const selectedCompare = available.includes(compare)
    ? compare
    : (available[0] ?? '');

  const series = buildSeries(
    results,
    ['harbor', selectedCompare].filter(Boolean),
    visibleYears
  );
  const maxMidpoint = Math.max(
    1,
    ...series.flatMap((item) => item.points.map((point) => point.value))
  );
  const roughStep = maxMidpoint / 4;
  const stepMagnitude = Math.pow(
    10,
    Math.floor(Math.log10(roughStep || 1))
  );
  const stepNormalized = roughStep / stepMagnitude;
  const step =
    (stepNormalized <= 1
      ? 1
      : stepNormalized <= 2
        ? 2
        : stepNormalized <= 5
          ? 5
          : 10) * stepMagnitude;
  const axisMax = Math.max(step, Math.ceil(maxMidpoint / step) * step);
  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;
  const xForYear = (year: number) =>
    visibleYears === 1
      ? PADDING.left + plotWidth / 2
      : PADDING.left + ((year - 1) / (visibleYears - 1)) * plotWidth;
  const yForValue = (value: number) =>
    PADDING.top + plotHeight - (value / axisMax) * plotHeight;

  const gridValues = Array.from(
    { length: Math.round(axisMax / step) + 1 },
    (_, index) => index * step
  );
  const pointFor = (item: ChartSeries, year: number) =>
    item.points.find((point) => point.year === year);

  const markers = Array.from({ length: visibleYears }, (_, index) => index + 1)
    .flatMap((year) => {
      const entries: Array<{
        item: ChartSeries;
        point: ChartPoint;
        x: number;
        y: number;
      }> = [];

      for (const item of series) {
        const point = pointFor(item, year);
        if (!point) continue;
        entries.push({
          item,
          point,
          x: xForYear(year),
          y: yForValue(point.value),
        });
      }

      entries.sort((a, b) => a.y - b.y);
      let previousLabelY = Number.NEGATIVE_INFINITY;

      return entries.map((entry) => {
        const labelY = Math.max(
          PADDING.top + 10,
          entry.y - 10,
          previousLabelY + 12
        );
        previousLabelY = labelY;
        return { ...entry, labelY };
      });
    });

  return (
    <figure className='rounded-2xl border border-gray-200 bg-white p-4 shadow-sm sm:p-6'>
      <div className='flex flex-wrap items-start justify-between gap-3'>
        <div>
          <h3 className='text-lg font-bold text-gray-900'>
            Estimated cumulative cost
          </h3>
        </div>
        <div className='flex flex-wrap items-center gap-2 text-xs'>
          <span className='inline-flex min-h-9 items-center rounded-full bg-primary/10 px-3 font-semibold text-primary'>
            Harbor
          </span>
          <span className='text-gray-400'>vs</span>
          <select
            aria-label='Comparison app'
            value={selectedCompare}
            onChange={(event) => onCompareChange(event.target.value)}
            className='min-h-9 rounded-lg border border-gray-200 bg-white px-2 py-1 font-medium text-gray-800'
          >
            {competitors.map((result) => (
              <option key={result.app.slug} value={result.app.slug}>
                {result.app.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className='mt-4 flex flex-wrap gap-x-5 gap-y-1 text-xs text-gray-600'>
        {series.map((item) => {
          const point = pointFor(item, visibleYears);
          return (
            <span key={item.slug} className='inline-flex items-center gap-1.5'>
              <span
                className='h-2.5 w-2.5 rounded-full'
                style={{ backgroundColor: item.color }}
              />
              <span className='font-semibold text-gray-900'>{item.name}</span>
              {point ? <span>{formatAxis(point.value)}</span> : null}
            </span>
          );
        })}
      </div>

      <div className='mt-1 overflow-x-auto'>
        <svg
          viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
          role='img'
          aria-label={`Estimated cumulative cost over ${visibleYears} year${
            visibleYears === 1 ? '' : 's'
          }`}
          className='h-auto w-full min-w-[560px]'
        >
          {gridValues.map((value) => (
            <g key={value}>
              <line
                x1={PADDING.left}
                x2={WIDTH - PADDING.right}
                y1={yForValue(value)}
                y2={yForValue(value)}
                stroke='#E5E7EB'
                strokeWidth='1'
              />
              <text
                x={PADDING.left - 10}
                y={yForValue(value) + 4}
                textAnchor='end'
                className='fill-gray-500 text-[11px]'
              >
                {formatAxis(value)}
              </text>
            </g>
          ))}

          {Array.from({ length: visibleYears }, (_, index) => index + 1).map(
            (year) => (
              <text
                key={year}
                x={xForYear(year)}
                y={HEIGHT - PADDING.bottom + 22}
                textAnchor='middle'
                className='fill-gray-500 text-[12px]'
              >
                {year === 1 ? '1 yr' : `${year} yr`}
              </text>
            )
          )}

          {series.map((item) => (
            <polyline
              key={item.slug}
              fill='none'
              stroke={item.color}
              strokeWidth='3'
              strokeLinejoin='round'
              strokeLinecap='round'
              points={item.points
                .map(
                  (point) =>
                    `${xForYear(point.year)},${yForValue(point.value)}`
                )
                .join(' ')}
            />
          ))}

          {markers.map(({ item, point, x, y, labelY }) => {
            const textAnchor =
              point.year === 1
                ? 'start'
                : point.year === visibleYears
                  ? 'end'
                  : 'middle';
            const labelX =
              point.year === 1
                ? x + 4
                : point.year === visibleYears
                  ? x - 4
                  : x;

            return (
              <g key={`marker-${item.slug}-${point.year}`}>
                <circle cx={x} cy={y} r='3.5' fill={item.color} />
                <text
                  x={labelX}
                  y={labelY}
                  textAnchor={textAnchor}
                  className='text-[10px] font-semibold'
                  fill={item.color}
                  stroke='#FFFFFF'
                  strokeWidth='3'
                  strokeLinejoin='round'
                  paintOrder='stroke'
                >
                  {formatAxis(point.value)}
                </text>
              </g>
            );
          })}

        </svg>
      </div>

      <figcaption className='mt-3 text-xs text-gray-500'>
        Prices are for one paid parent. If both parents subscribe, per-person
        apps double; household plans stay the same.
      </figcaption>

      <div className='sr-only'>
        <table>
          <caption>Estimated cumulative cost by year</caption>
          <thead>
            <tr>
              <th scope='col'>App</th>
              {Array.from({ length: visibleYears }, (_, index) => (
                <th key={index} scope='col'>
                  Year {index + 1}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {series.map((item) => (
              <tr key={item.slug}>
                <th scope='row'>{item.name}</th>
                {item.points.map((point) => (
                  <td key={point.year}>
                    {point.range.high > point.range.low + 0.01
                      ? `$${Math.round(point.range.low)} to $${Math.round(
                          point.range.high
                        )}`
                      : `$${Math.round(point.range.low)}`}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </figure>
  );
}
